import { Typography } from "antd";
import {
  CloudUploadOutlined,
  ScanOutlined,
  FileSearchOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";

const STEPS = [
  {
    icon: <CloudUploadOutlined />,
    label: "Uploading file",
    desc: "Securely transferring your document...",
    color: "#1677ff",
  },
  {
    icon: <ScanOutlined />,
    label: "Scanning document",
    desc: "Detecting document type and layout...",
    color: "#8b5cf6",
  },
  {
    icon: <FileSearchOutlined />,
    label: "Extracting data",
    desc: "Reading text and key fields with OCR...",
    color: "#f59e0b",
  },
  {
    icon: <SafetyCertificateOutlined />,
    label: "Verifying results",
    desc: "Almost done, validating extracted info...",
    color: "#10b981",
  },
];

const FullscreenLoader = ({
  visible,
  text,
}: {
  visible: boolean;
  text?: string;
}) => {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (!visible) {
      setActiveStep(0);
      return;
    }

    const interval = setInterval(() => {
      setActiveStep((prev) => (prev < STEPS.length - 1 ? prev + 1 : prev));
    }, 2800);

    return () => clearInterval(interval);
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.65)",
        backdropFilter: "blur(6px)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "all",
      }}
    >
      <div
        style={{
          padding: "36px 44px 32px",
          borderRadius: 20,
          background: "#ffffff",
          boxShadow: "0 25px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.04)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 28,
          minWidth: 340,
          maxWidth: 420,
        }}
      >
        {/* Animated icon */}
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: 20,
            background: `linear-gradient(135deg, ${STEPS[activeStep].color}15, ${STEPS[activeStep].color}25)`,
            border: `2px solid ${STEPS[activeStep].color}30`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 32,
            color: STEPS[activeStep].color,
            transition: "all 0.5s ease",
            animation: "pulse-icon 2s ease-in-out infinite",
          }}
        >
          {STEPS[activeStep].icon}
        </div>

        {/* Title */}
        <div style={{ textAlign: "center" }}>
          <Typography.Text
            strong
            style={{
              fontSize: 17,
              color: "#0f172a",
              display: "block",
              marginBottom: 4,
            }}
          >
            {text || "Processing..."}
          </Typography.Text>
          <Typography.Text
            style={{
              fontSize: 13,
              color: "#64748b",
              transition: "all 0.4s ease",
            }}
          >
            {STEPS[activeStep].desc}
          </Typography.Text>
        </div>

        {/* Step progress */}
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 10 }}>
          {STEPS.map((step, i) => {
            const isActive = i === activeStep;
            const isComplete = i < activeStep;
            const isPending = i > activeStep;

            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "8px 14px",
                  borderRadius: 10,
                  background: isActive
                    ? `${step.color}08`
                    : isComplete
                    ? "#f8fafc"
                    : "transparent",
                  border: isActive
                    ? `1px solid ${step.color}25`
                    : "1px solid transparent",
                  transition: "all 0.4s ease",
                  opacity: isPending ? 0.4 : 1,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: isComplete
                      ? `${step.color}15`
                      : isActive
                      ? `${step.color}20`
                      : "#f1f5f9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    color: isComplete || isActive ? step.color : "#94a3b8",
                    transition: "all 0.4s ease",
                    flexShrink: 0,
                  }}
                >
                  {isComplete ? (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M2.5 7L5.5 10L11.5 4"
                        stroke={step.color}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    step.icon
                  )}
                </div>
                <Typography.Text
                  style={{
                    fontSize: 13,
                    fontWeight: isActive ? 600 : 500,
                    color: isActive
                      ? "#0f172a"
                      : isComplete
                      ? "#64748b"
                      : "#94a3b8",
                    transition: "all 0.4s ease",
                  }}
                >
                  {step.label}
                </Typography.Text>

                {isActive && (
                  <div
                    style={{
                      marginLeft: "auto",
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: step.color,
                      animation: "blink-dot 1.2s ease-in-out infinite",
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes pulse-icon {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.06); }
        }
        @keyframes blink-dot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};

export default FullscreenLoader;
